/**
 * api.js — The single place the client talks to the backend.
 *
 * Centralising fetch here means error shapes, auth headers and abort handling are
 * consistent everywhere, and no component ever constructs a URL by hand.
 */

/** Thrown for any non-2xx response, carrying the server's structured error. */
export class ApiError extends Error {
  constructor(message, { status, code, details } = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }

  /** True when the failure is the user's input rather than a server fault. */
  get isClientError() {
    return this.status >= 400 && this.status < 500;
  }
}

/**
 * Supplies the current Firebase ID token for admin requests.
 *
 * Injected by the auth provider at start-up rather than imported directly, so
 * this module has no dependency on Firebase and the public pages never pull the
 * auth SDK into their bundle.
 *
 * @type {null | (() => Promise<string | null>)}
 */
let tokenProvider = null;

export function setAuthTokenProvider(provider) {
  tokenProvider = provider;
}

/**
 * Perform a request against the API.
 *
 * @param {string} path            Path beginning with `/api`.
 * @param {object} [options]
 * @param {string} [options.method='GET']
 * @param {object} [options.body]  Serialised as JSON.
 * @param {object} [options.query] Appended as a query string, skipping empties.
 * @param {boolean} [options.auth=false] Attach the admin bearer token.
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<any>} Parsed JSON body.
 */
async function request(path, { method = 'GET', body, query, auth = false, signal } = {}) {
  const url = new URL(path, window.location.origin);

  for (const [key, value] of Object.entries(query || {})) {
    if (value !== undefined && value !== null && value !== '') {
      url.searchParams.set(key, String(value));
    }
  }

  const headers = {};
  if (body !== undefined && !(body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }

  if (auth) {
    const token = await tokenProvider?.();
    if (!token) {
      throw new ApiError('You are signed out. Please sign in again.', { status: 401, code: 'UNAUTHORIZED' });
    }
    headers.Authorization = `Bearer ${token}`;
  }

  let response;
  try {
    response = await fetch(url, {
      method,
      headers,
      signal,
      body: body instanceof FormData ? body : body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch (err) {
    // An aborted request is a normal consequence of navigation, not a failure —
    // let callers distinguish it from a genuine network problem.
    if (err.name === 'AbortError') throw err;
    throw new ApiError('Could not reach the server. Check your connection and try again.', {
      status: 0,
      code: 'NETWORK_ERROR',
    });
  }

  if (response.status === 204) return null;

  const isJson = (response.headers.get('content-type') || '').includes('application/json');
  const payload = isJson ? await response.json().catch(() => null) : null;

  if (!response.ok) {
    const error = payload?.error || {};
    throw new ApiError(error.message || `Request failed with status ${response.status}`, {
      status: response.status,
      code: error.code,
      details: error.details,
    });
  }

  return payload;
}

// ─── Public catalogue ─────────────────────────────────────────────────────────

export const api = {
  health: (signal) => request('/api/health', { signal }),

  listProducts: (query, signal) => request('/api/products', { query, signal }),

  getProduct: (slug, signal) => request(`/api/products/${encodeURIComponent(slug)}`, { signal }),

  getEmiSchedule: (slug, { variantId, tenureMonths }, signal) =>
    request(`/api/products/${encodeURIComponent(slug)}/emi-schedule`, {
      query: { variantId, tenureMonths },
      signal,
    }),

  getEmiQuote: (amount, fundType, signal) => request('/api/emi/quote', { query: { amount, fundType }, signal }),

  /**
   * Records a view. Analytics must never interfere with the page, so callers
   * treat a rejection here as a no-op.
   */
  recordView: (slug) => request(`/api/products/${encodeURIComponent(slug)}/view`, { method: 'POST' }),

  recordProceed: (slug, selection) =>
    request(`/api/products/${encodeURIComponent(slug)}/proceed`, { method: 'POST', body: selection }),

  // ─── Admin (all require a bearer token) ────────────────────────────────────

  admin: {
    session: (signal) => request('/api/admin/session', { auth: true, signal }),

    analytics: (days, signal) => request('/api/admin/analytics', { auth: true, query: { days }, signal }),

    leads: (limit, signal) => request('/api/admin/leads', { auth: true, query: { limit }, signal }),

    listProducts: (signal) => request('/api/admin/products', { auth: true, signal }),

    getProduct: (id, signal) => request(`/api/admin/products/${encodeURIComponent(id)}`, { auth: true, signal }),

    createProduct: (payload) => request('/api/admin/products', { method: 'POST', body: payload, auth: true }),

    updateProduct: (id, payload) =>
      request(`/api/admin/products/${encodeURIComponent(id)}`, { method: 'PUT', body: payload, auth: true }),

    setStatus: (id, isActive) =>
      request(`/api/admin/products/${encodeURIComponent(id)}/status`, {
        method: 'PATCH',
        body: { isActive },
        auth: true,
      }),

    deleteProduct: (id) =>
      request(`/api/admin/products/${encodeURIComponent(id)}`, { method: 'DELETE', auth: true }),

    uploadImage: (file) => {
      const form = new FormData();
      form.append('image', file);
      return request('/api/admin/uploads', { method: 'POST', body: form, auth: true });
    },
  },
};
